import { useEffect } from 'react';
import { trackPWAEvent } from '@/lib/analytics';

type BeforeInstallPromptOutcome = 'accepted' | 'dismissed';

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{
    outcome: BeforeInstallPromptOutcome;
    platform: string;
  }>;
  prompt: () => Promise<void>;
}

const isStandalone = () => {
  const displayModeStandalone = window.matchMedia('(display-mode: standalone)').matches;
  const iosStandalone = Boolean((window.navigator as Navigator & { standalone?: boolean }).standalone);
  return displayModeStandalone || iosStandalone;
};

export function PWAInstallTracker() {
  useEffect(() => {
    const handleBeforeInstallPrompt = (event: Event) => {
      const installEvent = event as BeforeInstallPromptEvent;
      trackPWAEvent('install_prompt_available', installEvent.platforms?.join(',') || 'unknown');

      installEvent.userChoice
        .then(({ outcome, platform }) => {
          if (outcome === 'accepted') {
            trackPWAEvent('install_prompt_accepted', platform);
          } else {
            trackPWAEvent('install_prompt_dismissed', platform);
          }
        })
        .catch(() => {
          // no-op: userChoice may reject depending on browser behavior
        });
    };

    const handleAppInstalled = () => {
      trackPWAEvent('installed');
    };

    const mediaQuery = window.matchMedia('(display-mode: standalone)');

    const trackStandaloneOpen = () => {
      if (isStandalone()) {
        trackPWAEvent('opened_standalone');
      }
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt as EventListener);
    window.addEventListener('appinstalled', handleAppInstalled);
    mediaQuery.addEventListener('change', trackStandaloneOpen);

    trackStandaloneOpen();

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt as EventListener);
      window.removeEventListener('appinstalled', handleAppInstalled);
      mediaQuery.removeEventListener('change', trackStandaloneOpen);
    };
  }, []);

  return null;
}
